import React, {useEffect, useState} from 'react';

import http from 'services/http';
import {alerta} from 'components/Alert';
import {useGetUserPermission} from 'hook/permissions';
import {moedaInput, moedaDisplay, parseMoeda} from 'utils/moedaInput';
import {trataErroEsportivas, AvisoModuloBloqueado} from '../shared';

import styles from './styles.module.css';

// Tipos: bool (Sim/Não) · moeda (R$, usa moedaInput) · inteiro · odd (decimal) · hora (HH:MM)
// Campos "anulavel" aceitam vazio → enviado como null.
const GRUPOS = [
    {
        titulo: 'Geral',
        campos: [
            {campo: 'active_bets', tipo: 'bool', label: 'Apostas esportivas ativas', help: 'Liga/desliga a modalidade na banca'},
            {campo: 'min_guess_per_bet', tipo: 'inteiro', label: 'Mínimo de palpites por bilhete'},
            {campo: 'max_guess_per_bet', tipo: 'inteiro', label: 'Máximo de palpites por bilhete'},
        ],
    },
    {
        titulo: 'Limites de valor',
        campos: [
            {campo: 'min_simple_bet_value_pre_game', tipo: 'moeda', label: 'Valor mínimo da aposta simples'},
            {campo: 'max_simple_bet_value_pre_game', tipo: 'moeda', label: 'Valor máximo da aposta simples'},
            {campo: 'min_multiple_bet_value_pre_game', tipo: 'moeda', label: 'Valor mínimo da aposta múltipla'},
            {campo: 'max_multiple_bet_value_pre_game', tipo: 'moeda', label: 'Valor máximo da aposta múltipla'},
            {campo: 'max_prize', tipo: 'moeda', anulavel: true, label: 'Prêmio máximo por bilhete', help: 'Deixe vazio para não limitar'},
        ],
    },
    {
        titulo: 'Limites de odd',
        campos: [
            {campo: 'min_bet_odd_pre_game', tipo: 'odd', label: 'Odd mínima aceita por seleção'},
            {campo: 'max_odd', tipo: 'odd', label: 'Odd máxima por seleção'},
            {campo: 'block_odd_less_than', tipo: 'odd', label: 'Bloquear seleção com odd abaixo de'},
            {campo: 'max_bet_odd', tipo: 'odd', label: 'Odd total máxima do bilhete', help: 'Acima disso a comissão é calculada por este teto'},
            {campo: 'no_commission_below_odd', tipo: 'odd', label: 'Sem comissão abaixo da odd total', help: 'Bilhete com odd total abaixo disso não gera comissão'},
        ],
    },
    {
        titulo: 'Cancelamento & janela',
        campos: [
            {campo: 'minutes_to_cancel_bet', tipo: 'inteiro', label: 'Minutos para o operador cancelar a aposta', help: 'Janela após a criação do bilhete'},
            {campo: 'game_days_ahead', tipo: 'inteiro', label: 'Dias de eventos futuros ofertados'},
        ],
    },
    {
        titulo: 'Bloqueio por horário',
        campos: [
            {campo: 'bet_block_period_start', tipo: 'hora', anulavel: true, label: 'Início do bloqueio diário', help: 'Deixe vazio para não bloquear'},
            {campo: 'bet_block_period_end', tipo: 'hora', anulavel: true, label: 'Fim do bloqueio diário', help: 'Deixe vazio para não bloquear'},
        ],
    },
];

const CAMPOS = GRUPOS.flatMap((grupo) => grupo.campos);

// Converte o valor vindo da API para o formato do input
function paraInput(definicao, valor) {
    if (valor === null || valor === undefined) {
        return '';
    }

    if (definicao.tipo === 'bool') {
        return valor ? '1' : '0';
    }

    if (definicao.tipo === 'moeda') {
        return moedaDisplay(valor);
    }

    return String(valor);
}

// Converte o valor do input para o payload do PUT (undefined = não enviar)
function paraPayload(definicao, valor) {
    if (definicao.tipo === 'bool') {
        return valor === '1';
    }

    if (valor === '') {
        return definicao.anulavel ? null : undefined;
    }

    if (definicao.tipo === 'moeda') {
        return parseMoeda(valor);
    }

    if (definicao.tipo === 'inteiro') {
        const inteiro = parseInt(valor, 10);
        return isNaN(inteiro) ? undefined : inteiro;
    }

    if (definicao.tipo === 'odd') {
        const numero = parseFloat(valor);
        return isNaN(numero) ? undefined : numero;
    }

    return valor;
}

export default function Configuracoes() {
    const [valores, setValores] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [carregaPost, setCarregaPost] = useState(false);
    const [moduloBloqueado, setModuloBloqueado] = useState(false);

    const permissoes = useGetUserPermission();
    const podeEditar = permissoes && permissoes.includes('Editar settings esportivas');

    useEffect(() => {
        const requestSettings = async () => {
            try {
                const response = await http.get('/esportivas/settings');
                const settings = response.data.data;

                const iniciais = {};
                CAMPOS.forEach((definicao) => {
                    iniciais[definicao.campo] = paraInput(definicao, settings[definicao.campo]);
                });

                setValores(iniciais);
            } catch (e) {
                trataErroEsportivas(e, setModuloBloqueado);
            }

            setIsLoading(false);
        };

        requestSettings();
    }, []);

    const atualizarCampo = (campo, valor) => {
        setValores((prev) => ({...prev, [campo]: valor}));
    };

    const salvar = async () => {
        try {
            setCarregaPost(true);

            const payload = {};
            CAMPOS.forEach((definicao) => {
                const valor = paraPayload(definicao, valores[definicao.campo]);
                if (valor !== undefined) {
                    payload[definicao.campo] = valor;
                }
            });

            const response = await http.put('/esportivas/settings', payload);

            const settings = response.data.data;
            const atualizados = {};
            CAMPOS.forEach((definicao) => {
                atualizados[definicao.campo] = paraInput(definicao, settings[definicao.campo]);
            });
            setValores(atualizados);

            alerta(response.data);
        } catch (e) {
            trataErroEsportivas(e, setModuloBloqueado);
        }

        setCarregaPost(false);
    };

    const renderInput = (definicao) => {
        const valor = valores[definicao.campo] ?? '';
        const desabilitado = !podeEditar || carregaPost;

        if (definicao.tipo === 'bool') {
            return (
                <select
                    className="form-control"
                    style={{width: 200}}
                    disabled={desabilitado}
                    value={valor}
                    onChange={(e) => atualizarCampo(definicao.campo, e.target.value)}
                >
                    <option value="1">Sim</option>
                    <option value="0">Não</option>
                </select>
            );
        }

        if (definicao.tipo === 'moeda') {
            return (
                <input
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    style={{width: 200}}
                    placeholder={definicao.anulavel ? 'Sem limite' : '0,00'}
                    disabled={desabilitado}
                    value={valor}
                    onChange={(e) => atualizarCampo(
                        definicao.campo,
                        e.target.value === '' ? '' : moedaInput(e.target.value)
                    )}
                />
            );
        }

        if (definicao.tipo === 'hora') {
            return (
                <input
                    type="time"
                    className="form-control"
                    style={{width: 200}}
                    disabled={desabilitado}
                    value={valor}
                    onChange={(e) => atualizarCampo(definicao.campo, e.target.value)}
                />
            );
        }

        return (
            <input
                type="number"
                inputMode="numeric"
                step={definicao.tipo === 'odd' ? '0.01' : '1'}
                min={definicao.tipo === 'odd' ? 1 : 0}
                className="form-control"
                style={{width: 200}}
                disabled={desabilitado}
                value={valor}
                onChange={(e) => atualizarCampo(definicao.campo, e.target.value)}
            />
        );
    };

    if (moduloBloqueado) {
        return (
            <>
                <div className="titulo-principal">Configurações de Esportivas</div>
                <AvisoModuloBloqueado/>
            </>
        );
    }

    return (
        <>
            <div className="titulo-principal">Configurações de Esportivas</div>

            {isLoading && <div className="loader"/>}

            {!isLoading && (
                <div className={styles.conteudoPrincipal}>
                    {!podeEditar && (
                        <div className={styles.avisoLeitura}>
                            Você não possui a permissão "Editar settings esportivas" — somente leitura.
                        </div>
                    )}

                    {GRUPOS.map((grupo) => (
                        <div key={grupo.titulo}>
                            <div className={styles.tituloGrupo}>{grupo.titulo}</div>

                            {grupo.campos.map((definicao) => (
                                <div key={definicao.campo} className={styles.formGroup}>
                                    <div className={styles.label}>
                                        <label>{definicao.label}:</label>
                                        {definicao.help && (
                                            <small className={styles.help}>{definicao.help}</small>
                                        )}
                                    </div>
                                    <div className={styles.input}>
                                        {renderInput(definicao)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}

                    {podeEditar && (
                        <div className={styles.buttonStyle}>
                            <button
                                className="btn btn-success"
                                onClick={() => salvar()}
                                disabled={carregaPost}
                            >
                                Salvar
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
